/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import GoogleMapReact from 'google-map-react';
import { useTranslation } from 'react-i18next';

import Marker from './Marker';
import GoogleMapChips from './GoogleMapChips';
import ErrorScreen from './ErrorScreen';
import NoDataScreen from './NoDataScreen';
import { ChipStatus, PROCESS_EVENTS } from '../../../Constants/App';
import { Icons } from '../../../Constants/Icons';

import GoogleMapBottomBarOptions from './GoogleMapBottomBarOptions';
import './index.css';
import {
  BUSIEST_AREA_PADDING,
  FACILITY_MARKER_ICON_PX,
  FilterType,
  SINGLE_FACILITY_ZOOM,
  STATUS_MARKER_ICON_PX,
  STATUS_MARKER_ICONS,
} from './Marker.Model';
import { getBusiestAreaFacilities } from './GoogleMap.Utils';
import { WalkingLoader } from '../MapLoader/MapLoader';
import theme from '../../../Themes/theme';
import { mapStyles } from './InfoWindow.Style';
import { hasEventId } from '../../../Utilities/utils';
import { IProcessEvents } from '../../../../routes/config';

interface IAddressDetails {
  buildingName: string;
  scheduled: number;
  completed: number;
  inProgress: number;
  noShow: number;
  noJob: number;
  address: string;
}

interface IServiceCategory {
  serviceCategory: string;
  serviceCategoryName: string;
  totalTasks: number;
  inProgress: number;
  completed: number;
  approvedBySupervisor: number;
  approvedByCustomer: number;
  rejectedByCustomer: number;
}

interface IGoogleMapProps {
  loading: boolean;
  error: boolean;
  allData: any;
  showNoDataScreen: boolean;
  fetchData: () => void;
  locationData: any;
  processEvents?: IProcessEvents[];
  isCustomerOrNonVelocitiUsersLoggedIn?: boolean;
}

interface IFacilityItem {
  facility: string;
  facilityName: string;
  buildingName?: string;
  latitude: number;
  longitude: number;
  logoUrl: string;
  logourl: string;
  // Full address from the map summary (street, city, state code and postal code in one string).
  address?: string;
  facilityStatus: string;
  serviceCategory: IServiceCategory[];
  scheduled: [];
  completed: [];
  inProgress: [];
  noShow: [];
  noJob: [];
  facilityId: string;
}

function createMapOptions(maps: any) {
  // next props are exposed at maps
  // "Animation", "ControlPosition", "MapTypeControlStyle", "MapTypeId",
  // "NavigationControlStyle", "ScaleControlStyle", "StrokePosition", "SymbolPath", "ZoomControlStyle",
  // "DirectionsStatus", "DirectionsTravelMode", "DirectionsUnitSystem", "DistanceMatrixStatus",
  // "DistanceMatrixElementStatus", "ElevationStatus", "GeocoderLocationType", "GeocoderStatus", "KmlLayerStatus",
  // "MaxZoomStatus", "StreetViewStatus", "TransitMode", "TransitRoutePreference", "TravelMode", "UnitSystem"
  // const { ControlPosition, ZoomControlStyle } = maps;

  return {
    zoomControlOptions: {
      position: maps.ControlPosition.RIGHT_CENTER,
      style: maps.ZoomControlStyle.SMALL,
    },
    mapTypeControlOptions: {
      position: maps.ControlPosition.TOP_RIGHT,
    },
    mapTypeControl: true,
  };
}

type WorkOrder = { workOrderId?: string; workOrderName?: string; jobId?: string; customerId?: string };

const GoogleMap = ({
  loading,
  error,
  allData,
  showNoDataScreen,
  fetchData,
  locationData,
  processEvents,
  isCustomerOrNonVelocitiUsersLoggedIn,
}: IGoogleMapProps) => {
  const { lat = 0, long = 0 } = locationData || {};
  const { t } = useTranslation(['homePage', 'altTexts']);
  // The map and its API, set again each time the map is created (it is shown again after every load).
  const [mapApi, setMapApi] = useState<{ map: any; maps: any } | null>(null);
  const defaultProps = {
    center: {
      lat: lat,
      lng: long,
    },
    zoom: 6,
  };

  const getMarkerData = useMemo(() => {
    if (!allData || typeof allData !== 'object') return [];

    return [
      ...(Array.isArray(allData.facilityData) ? allData.facilityData : []),
      ...(Array.isArray(allData.facilitiesWithoutShifts) ? allData.facilitiesWithoutShifts : []),
    ];
  }, [allData]);

  const [selectedChip, setSelectedChip] = useState(FilterType.All);
  const [selectedDate, setSelectedDate] = useState('');

  const chipsData = useMemo(
    () => [
      {
        label: t(ChipStatus['Facilities']),
        variant: 'outlined',
        count: allData.totalFacilities,
        borderColor: theme.palette.warning.main,
        textColor: theme.palette.text.primary,
        hoverColor: theme.palette.secondary.dark,
        clickColor: theme.palette.secondary.dark,
        type: FilterType.All,
        icon: <img src={Icons.FacilityIcon} alt={t('altTexts:scheduled')} style={{ width: '1rem', height: '1rem' }} />,
      },
      {
        label: t(ChipStatus['JobScheduled']),
        variant: 'outlined',
        count: allData.scheduled,
        borderColor: theme.palette.info.dark,
        textColor: theme.palette.text.primary,
        hoverColor: theme.palette.secondary.customColor8,
        clickColor: theme.palette.secondary.customColor8,
        type: FilterType.Scheduled,
        icon: <img src={Icons.PendingReviews} alt={t('altTexts:scheduled')} />,
      },
      {
        label: t(ChipStatus['No Show']),
        variant: 'outlined',
        count: allData.noShows,
        borderColor: theme.palette.error.main,
        textColor: theme.palette.text.primary,
        hoverColor: theme.palette.error.light,
        clickColor: theme.palette.error.light,
        type: FilterType.NoShow,
        icon: <img src={Icons.NoShowIcon} alt={t('altTexts:noShow')} />,
      },
      {
        label: t(ChipStatus['In progress']),
        variant: 'outlined',
        count: allData.inProgress,
        borderColor: theme.palette.secondary.contrastText,
        textColor: theme.palette.text.primary,
        hoverColor: theme.palette.secondary.main,
        clickColor: theme.palette.secondary.main,
        type: FilterType.InProgress,
        icon: <img src={Icons.InProgressIcon} alt={t('altTexts:inprogress')} />,
      },
      {
        label: t(ChipStatus['Complete']),
        variant: 'outlined',
        count: allData.completed,
        borderColor: theme.palette.success.main,
        textColor: theme.palette.text.primary,
        hoverColor: theme.palette.success.light,
        clickColor: theme.palette.success.light,
        type: FilterType.Completed,
        icon: <img src={Icons.ApprovedIcon} alt={t('altTexts:complete')} />,
      },
      {
        label: t(ChipStatus['NoJob']),
        variant: 'outlined',
        count: allData?.facilitiesWithoutShifts?.length,
        borderColor: theme.palette.warning.dark,
        textColor: theme.palette.text.primary,
        hoverColor: theme.palette.warning.light,
        clickColor: theme.palette.warning.light,
        type: FilterType.NoJob,
        icon: <img src={Icons.InCompleteIcon} alt={t('altTexts:noJob')} />,
      },
    ],
    [allData, t]
  );

  const getMapContent = () => {
    if (showNoDataScreen) {
      return <NoDataScreen />;
    }
    if (selectedChip) {
      if (getMarkerData.length > 0) {
        return renderMap();
      } else {
        return <NoDataScreen />;
      }
    }

    return null;
  };

  const renderMapOrNoData = () => (
    <div className="reactMap" style={mapStyles.mapContainer}>
      {getMapContent()}
    </div>
  );

  const countNoJobFacilities = (facilities: IFacilityItem[]): Record<string, number> => {
    return facilities.reduce(
      (acc, item) => {
        if (item.facilityStatus === 'noJob') {
          if (acc[item.facilityId]) {
            acc[item.facilityId] += 1;
          } else {
            acc[item.facilityId] = 1;
          }
        }
        return acc;
      },
      {} as Record<string, number>
    );
  };

  const getFilterType = (item: IFacilityItem): FilterType => {
    if (item.inProgress?.length > 0) return FilterType.InProgress;
    if (item.completed?.length > 0) return FilterType.Completed;
    if (item.noShow?.length > 0) return FilterType.NoShow;
    if (item.scheduled?.length > 0) return FilterType.Scheduled;
    return FilterType.NoJob;
  };

  // Facilities with a usable position that match the selected chip: the markers drawn and the area the map fits to.
  const visibleMarkerData = useMemo(
    () =>
      getMarkerData.filter(
        (item: IFacilityItem) =>
          typeof item?.latitude === 'number' &&
          typeof item?.longitude === 'number' &&
          !!item.latitude &&
          !!item.longitude &&
          (selectedChip === FilterType.All || selectedChip === getFilterType(item))
      ),
    [getMarkerData, selectedChip]
  );

  const getWorkOrderId = (lists: WorkOrder[][]): string | null => {
    for (const list of lists) {
      if (list.length > 0) {
        return list[0]?.workOrderId ?? null;
      }
    }
    return null;
  };

  const getJobName = useCallback((lists: WorkOrder[][]): string | null => {
    for (const list of lists) {
      if (list?.length > 0) {
        return list?.[0]?.workOrderName ?? null;
      }
    }
    return null;
  }, []);

  const getJobId = (lists: WorkOrder[][]): string | null => {
    for (const list of lists) {
      if (list.length > 0) {
        return list[0]?.jobId ?? null;
      }
    }
    return null;
  };

  const getCustomerId = (lists: WorkOrder[][]): string | null => {
    for (const list of lists) {
      if (list.length > 0) {
        return list[0]?.customerId ?? null;
      }
    }
    return null;
  };

  const constructAddressDetails = (item: IFacilityItem, noJobCounts: Record<string, number>): IAddressDetails => {
    const {
      facilityName = '',
      scheduled = [],
      completed = [],
      inProgress = [],
      noShow = [],
      facility = '',
    } = item || {};

    return {
      buildingName: facilityName,
      scheduled: scheduled?.length,
      completed: completed?.length,
      inProgress: inProgress?.length,
      noShow: noShow?.length,
      noJob: noJobCounts[facility] || 0,
      address: item?.address?.trim() ?? '',
    };
  };

  const constructFacilityIcon = (logoUrl: string, logourl: string): string => {
    const photoReference = logoUrl || logourl;
    if (photoReference) {
      return `https://maps.googleapis.com/maps/api/place/photo?maxwidth=400&photo_reference=${photoReference}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string}`;
    }
    return '';
  };

  const noJobCounts = useMemo(() => countNoJobFacilities(getMarkerData), [getMarkerData]);

  const [selectedMarkerIndex, setSelectedMarkerIndex] = useState<number | null>(null);
  useEffect(() => setSelectedMarkerIndex(null), [visibleMarkerData]);

  // The map is only on screen when the data has loaded; forget the old one once it is gone, so the markers below are
  // only ever put on the map that is showing (the new one reports itself through onGoogleApiLoaded).
  useEffect(() => {
    if (loading || error || showNoDataScreen) setMapApi(null);
  }, [loading, error, showNoDataScreen]);

  useEffect(() => {
    if (!mapApi) return undefined;
    const { map, maps } = mapApi;
    const isAllChip = selectedChip === FilterType.All;
    const iconPx = isAllChip ? FACILITY_MARKER_ICON_PX : STATUS_MARKER_ICON_PX;
    const nativeMarkers = visibleMarkerData.flatMap((item: IFacilityItem, index: number) => {
      const iconUrl = isAllChip ? Icons.FacilityMapIcon : STATUS_MARKER_ICONS[getFilterType(item)];
      if (!iconUrl) return [];
      const nativeMarker = new maps.Marker({
        position: { lat: item.latitude, lng: item.longitude },
        map,
        optimized: true,
        icon: {
          url: iconUrl,
          scaledSize: new maps.Size(iconPx, iconPx),
          anchor: new maps.Point(iconPx / 2, iconPx / 2),
        },
      });
      nativeMarker.addListener('click', () => {
        // Zooms in on the clicked facility so it is clear where it is.
        map.setCenter({ lat: item.latitude, lng: item.longitude });
        if ((map.getZoom() ?? 0) < SINGLE_FACILITY_ZOOM) map.setZoom(SINGLE_FACILITY_ZOOM);
        setSelectedMarkerIndex(index);
      });
      return [nativeMarker];
    });
    return () => {
      nativeMarkers.forEach((nativeMarker: any) => {
        maps.event.clearInstanceListeners(nativeMarker);
        nativeMarker.setMap(null);
      });
    };
  }, [mapApi, visibleMarkerData, selectedChip]);

  // A status chip (e.g. Completed) zooms in on the area with most of its facilities so they are easy to find; the
  // others are a scroll away.
  useEffect(() => {
    if (!mapApi || selectedChip === FilterType.All || !visibleMarkerData.length) return;
    const { map, maps } = mapApi;
    const areaFacilities = getBusiestAreaFacilities(visibleMarkerData as IFacilityItem[]);
    if (areaFacilities.length === 1) {
      map.setCenter({ lat: areaFacilities[0].latitude, lng: areaFacilities[0].longitude });
      map.setZoom(SINGLE_FACILITY_ZOOM);
      return;
    }
    const bounds = new maps.LatLngBounds();
    areaFacilities.forEach((item) => bounds.extend({ lat: item.latitude, lng: item.longitude }));
    map.fitBounds(bounds, BUSIEST_AREA_PADDING);
    // Facilities next to each other would otherwise zoom the map in further than street level.
    maps.event.addListenerOnce(map, 'idle', () => {
      if ((map.getZoom() ?? 0) > SINGLE_FACILITY_ZOOM) map.setZoom(SINGLE_FACILITY_ZOOM);
    });
  }, [mapApi, visibleMarkerData, selectedChip]);

  const renderMarker = (item: IFacilityItem, index: number, type?: FilterType) => {
    const {
      facility = '',
      latitude,
      longitude,
      logoUrl = '',
      logourl = '',
      scheduled = [],
      completed = [],
      inProgress = [],
      noShow = [],
    } = item || {};
    const filterType = getFilterType(item);
    const workOrderId = getWorkOrderId([completed, scheduled, inProgress, noShow]);
    const jobId = getJobId([completed, scheduled, inProgress, noShow]);
    const jobName = getJobName([completed, scheduled, inProgress, noShow]);
    const customerId = getCustomerId([completed, scheduled, inProgress, noShow]);
    const addressDetails = constructAddressDetails(item, noJobCounts);
    const facilityIcon = constructFacilityIcon(logoUrl, logourl);
    return (
      <Marker
        key={`${facility}-${index}`}
        facilityId={facility}
        logoUrl={facilityIcon}
        data={item?.serviceCategory}
        lng={longitude}
        lat={latitude}
        type={filterType}
        chipType={type}
        addressDetails={addressDetails}
        selectedDate={selectedDate}
        workOrderId={workOrderId}
        jobId={jobId}
        jobName={jobName}
        customerId={customerId}
        defaultOpen
        onClose={() => setSelectedMarkerIndex(null)}
      />
    );
  };

  const renderSelectedMarker = (type?: FilterType) => {
    const selectedItem = selectedMarkerIndex === null ? undefined : visibleMarkerData[selectedMarkerIndex];
    return selectedItem ? renderMarker(selectedItem, selectedMarkerIndex as number, type) : null;
  };

  const renderMap = () => {
    return (
      <GoogleMapReact
        bootstrapURLKeys={{ key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string }}
        defaultCenter={{
          lat: visibleMarkerData[0]?.latitude ?? defaultProps.center.lat,
          lng: visibleMarkerData[0]?.longitude ?? defaultProps.center.lng,
        }}
        defaultZoom={defaultProps.zoom}
        options={createMapOptions}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={({ map, maps }) => {
          setMapApi({ map, maps });
        }}>
        {renderSelectedMarker(selectedChip)}
      </GoogleMapReact>
    );
  };

  const applyFilter = (type: FilterType) => {
    setSelectedChip(type);
  };

  return (
    <div style={{ width: '100%' }}>
      <GoogleMapChips
        loading={loading}
        error={error}
        chipsData={chipsData}
        selectedChip={selectedChip}
        applyFilter={applyFilter}
        fetchData={fetchData}
        setSelectedDate={setSelectedDate}
        processEvents={processEvents}
        isCustomerOrNonVelocitiUsersLoggedIn={isCustomerOrNonVelocitiUsersLoggedIn}
      />
      {((processEvents && hasEventId(processEvents, PROCESS_EVENTS.DashboardMapView)) ||
        isCustomerOrNonVelocitiUsersLoggedIn) && (
        <>
          <div
            style={{
              height: '43.75rem',
              backgroundColor: theme.palette.warning.contrastText,
              width: '95%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              margin: '0 auto',
              borderRadius: '1.25rem',
            }}>
            {loading ? (
              <WalkingLoader alt={t('altTexts:loading')} />
            ) : error ? (
              <ErrorScreen fetchData={fetchData} />
            ) : (
              renderMapOrNoData()
            )}
          </div>
          <GoogleMapBottomBarOptions loading={loading} error={error} allData={allData} />
        </>
      )}
    </div>
  );
};

export default GoogleMap;
